import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCreditCard, FiCheck, FiAlertCircle, FiExternalLink, FiSave, FiEye, FiEyeOff, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { stripeConnectService } from '../../services/stripeConnectService';

const DealerStripeSettings = () => {
  const { dealerId } = useAuth();
  const [status, setStatus] = useState({ connected: false, publishableKey: '', secretKey: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (dealerId) loadStatus();
  }, [dealerId]);

  const loadStatus = async () => {
    setLoading(true);
    const result = await stripeConnectService.checkStripeStatus(dealerId);
    setStatus(result);
    setPublishableKey(result.publishableKey || '');
    setSecretKey(result.secretKey || '');
    setLoading(false);
  };

  const handleSave = async () => {
    if (!publishableKey.startsWith('pk_')) {
      setMessage({ type: 'error', text: 'Publishable key must start with pk_' });
      return;
    }
    if (!secretKey.startsWith('sk_')) {
      setMessage({ type: 'error', text: 'Secret key must start with sk_' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    const result = await stripeConnectService.saveStripeKeys(dealerId, publishableKey, secretKey);
    if (result.error) {
      setMessage({ type: 'error', text: 'Failed to save keys: ' + (result.error.message || 'Unknown error') });
    } else {
      setMessage({ type: 'success', text: 'Stripe API keys saved' });
      await loadStatus();
    }
    setSaving(false);
  };

  const handleRemoveKeys = async () => {
    if (!confirm('Remove your Stripe API keys? You will not be able to accept payments until new keys are added.')) return;
    setSaving(true);
    await stripeConnectService.saveStripeKeys(dealerId, null, null);
    setPublishableKey('');
    setSecretKey('');
    setMessage({ type: 'success', text: 'Stripe keys removed' });
    await loadStatus();
    setSaving(false);
  };

  const maskKey = (key) => {
    if (!key || key.length < 12) return key;
    return key.substring(0, 7) + '••••••••' + key.substring(key.length - 4);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          status.connected ? 'bg-green-50' : 'bg-gray-100'
        }`}>
          <FiCreditCard className={`w-6 h-6 ${status.connected ? 'text-green-600' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">Stripe Payment Processing</h3>
          <p className="text-sm text-gray-500">
            {status.connected ? 'Your Stripe keys are active' : 'Enter your Stripe API keys to accept payments'}
          </p>
        </div>
        {status.connected && (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
            <FiCheck size={12} /> Active
          </span>
        )}
      </div>

      {/* Status message */}
      {message.text && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <FiCheck size={14} /> : <FiAlertCircle size={14} />}
          {message.text}
        </div>
      )}

      {/* API Key Fields */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Publishable Key</label>
          <input
            type="text"
            value={publishableKey}
            onChange={(e) => setPublishableKey(e.target.value)}
            placeholder="pk_live_... or pk_test_..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635bff] focus:border-[#635bff] font-mono text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">
            Find this in your <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#635bff] hover:underline">Stripe Dashboard → API Keys</a>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
          <div className="relative">
            <input
              type={showSecret ? 'text' : 'password'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder="sk_live_... or sk_test_..."
              className="w-full px-4 py-2.5 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635bff] focus:border-[#635bff] font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showSecret ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Your secret key is stored securely and never exposed to end users.</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving || !publishableKey || !secretKey}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#635bff] text-white rounded-lg hover:bg-[#5851db] disabled:bg-gray-300 font-medium text-sm transition-colors"
          >
            <FiSave size={14} />
            {saving ? 'Saving...' : 'Save Keys'}
          </button>

          {status.connected && (
            <>
              <a
                href="https://dashboard.stripe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
              >
                <FiExternalLink size={14} />
                Dashboard
              </a>
              <button
                onClick={handleRemoveKeys}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm"
              >
                <FiTrash2 size={14} />
              </button>
            </>
          )}
        </div>

        {!status.connected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
            <div className="flex items-start gap-2 text-amber-700 text-sm">
              <FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">No Stripe keys configured</p>
                <p className="text-xs text-amber-600 mt-1">
                  Customers cannot purchase sheds from your inventory until you enter valid Stripe API keys.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DealerStripeSettings;
